import { useState, useEffect, useCallback } from 'react'
import { History, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import api from '../../services/api'

const fmtDateTime = (s) => {
  if (!s) return '—'
  try {
    const d = new Date(s)
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch { return s }
}

const PAGE_SIZE = 50

export default function TodosLogins() {
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  const fetchLogins = useCallback(() => {
    setLoading(true)
    api.get('/auth/super-admin/todos-logins', {
      params: { search: search || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE }
    })
      .then(r => { setItems(r.data.items); setTotal(r.data.total) })
      .catch(() => { setItems([]); setTotal(0) })
      .finally(() => setLoading(false))
  }, [search, page])

  useEffect(() => { fetchLogins() }, [fetchLogins])

  useEffect(() => { setPage(0) }, [search])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <History size={24} /> Histórico de Logins
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Todos os logins realizados, em todos os organizadores — {total} registro(s)
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nome do usuário..."
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">Nenhum login encontrado.</div>
        ) : (
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Usuário</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Organizador</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">IP</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Dispositivo</th>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Data/hora</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map(l => (
                <tr key={l.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-900">{l.usuario_nome}</div>
                    {l.email && <div className="text-xs text-gray-400">{l.email}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{l.organizador_nome || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">{l.ip_address || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-500 truncate max-w-[260px]" title={l.user_agent}>{l.user_agent || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{fmtDateTime(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Página {page + 1} de {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
